import { useEffect, useState } from 'react';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import { Check, ClipboardList, X } from 'lucide-react';
import l from '../../../../../lang';
import { useDashboard } from '../../../../../store/dashboard';
import { usePayouts } from '../../../../../store/payouts';
import { PayoutContactListItem } from '../../../../../store/payouts/interfaces';
import { Button } from '../../../../common/button';
import './styles.scss';

interface RevalidateIdentityDialogProps {
  open: boolean;
  contact: PayoutContactListItem | null;
  beneficiaryId: number;
  onClose: () => void;
}

const RevalidateIdentityDialog = ({
  open,
  contact,
  beneficiaryId,
  onClose,
}: RevalidateIdentityDialogProps) => {
  const { merchantMe } = useDashboard();
  const { revalidatePayoutContact } = usePayouts();
  const [step, setStep] = useState<'confirm' | 'sent'>('confirm');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep('confirm');
      setSubmitting(false);
    }
  }, [contact?.id, open]);

  if (!contact) {
    return null;
  }

  const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();

  const handleConfirm = () => {
    if (!merchantMe?.id || !contact.manual_revalidation_eligible) {
      return;
    }

    setSubmitting(true);
    revalidatePayoutContact(
      String(merchantMe.id),
      String(beneficiaryId),
      () => {
        setSubmitting(false);
        setStep('sent');
      },
      () => setSubmitting(false)
    );
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: `revalidate-identity-dialog revalidate-identity-dialog--${step}` }}
    >
      {step === 'sent' && (
        <IconButton
          className="revalidate-identity-dialog__close"
          aria-label={l('common.close')}
          onClick={onClose}
        >
          <X size={20} aria-hidden />
        </IconButton>
      )}
      <DialogContent className="revalidate-identity-dialog__content">
        <span className="revalidate-identity-dialog__icon" aria-hidden>
          {step === 'confirm' ? (
            <ClipboardList size={22} strokeWidth={1.75} />
          ) : (
            <Check size={22} strokeWidth={1.75} />
          )}
        </span>

        {step === 'confirm' ? (
          <>
            <h2>{l('payoutContacts.revalidate.confirm.title')}</h2>
            <p>{l('payoutContacts.revalidate.confirm.body')}</p>
            {contact.manual_revalidation_fee_usd != null && (
              <p className="revalidate-identity-dialog__fee">
                {l('payoutContacts.revalidate.confirm.fee', contact.manual_revalidation_fee_usd)}
              </p>
            )}
            <div className="revalidate-identity-dialog__actions">
              <Button
                variant="outlined"
                disabled={submitting}
                onClick={onClose}
              >
                {l('common.cancel')}
              </Button>
              <Button
                variant="contained"
                disabled={
                  submitting ||
                  !merchantMe?.id ||
                  !contact.manual_revalidation_eligible
                }
                onClick={handleConfirm}
              >
                {l('payoutContacts.menu.revalidate')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2>{l('payoutContacts.revalidate.sent.title')}</h2>
            <p>{l('payoutContacts.revalidate.sent.body', fullName)}</p>
            <p>{l('payoutContacts.revalidate.sent.helper')}</p>
            <span className="revalidate-identity-dialog__more-info">
              {l('payoutContacts.revalidate.sent.moreInformation')}
            </span>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RevalidateIdentityDialog;
